import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import {
  Search, Plus, Edit2, Trash2, MapPin, Clock, Star, Users,
  FolderOpen, GripVertical, Check, X, ChevronDown, ChevronUp,
  Image, Loader2, Tag, Filter, Move
} from 'lucide-react';

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price || 0);
};

export default function UnifiedToursTab({ tours, categories, onRefresh, onEditTour, onDeleteTour }) {
  const [activeSubTab, setActiveSubTab] = useState('tours');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  
  // Category management
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '', slug: '', description: '', icon: 'Compass', color: '#22c55e', 
    image_url: '', is_active: true, sort_order: 0
  });
  
  // Tour category assignment
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Category tour management
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showAddTourDialog, setShowAddTourDialog] = useState(false);
  const [addingToCategory, setAddingToCategory] = useState(null);

  const iconOptions = [
    { value: 'Compass', label: 'Compass' },
    { value: 'Mountain', label: 'Mountain' },
    { value: 'Waves', label: 'Waves' },
    { value: 'UtensilsCrossed', label: 'Food' },
    { value: 'Moon', label: 'Night' },
    { value: 'Sun', label: 'Sun' },
    { value: 'Camera', label: 'Camera' },
    { value: 'Map', label: 'Map' },
    { value: 'Anchor', label: 'Marine' },
    { value: 'TreePine', label: 'Nature' },
  ];

  const colorPresets = [
    '#22c55e', '#06b6d4', '#f97316', '#8b5cf6', 
    '#ec4899', '#eab308', '#ef4444', '#3b82f6'
  ];

  // Filter tours
  const filteredTours = tours.filter(tour => {
    const matchesSearch = searchTerm === '' || 
      tour.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tour.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || tour.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Get tours for a category
  const getToursInCategory = (categorySlug) => {
    return tours.filter(t => t.category === categorySlug);
  };

  // Assign tour to category
  const handleAssignCategory = async () => {
    if (!selectedTour || !selectedCategory) return;
    
    setLoading(true);
    try {
      await adminAPI.updateTour(selectedTour.id, { category: selectedCategory });
      toast.success(`Tour assigned to ${selectedCategory}`);
      setShowAssignDialog(false);
      setSelectedTour(null);
      setSelectedCategory('');
      onRefresh();
    } catch (error) {
      toast.error('Failed to assign category');
    } finally {
      setLoading(false);
    }
  };

  // Remove tour from category (set to uncategorized)
  const handleRemoveFromCategory = async (tourId) => {
    setLoading(true);
    try {
      await adminAPI.updateTour(tourId, { category: 'other' });
      toast.success('Tour removed from category');
      onRefresh();
    } catch (error) {
      toast.error('Failed to remove tour');
    } finally {
      setLoading(false);
    }
  };

  // Category CRUD
  const handleSaveCategory = async () => {
    if (!categoryForm.name || !categoryForm.slug) {
      toast.error('Name and slug are required');
      return;
    }

    setLoading(true);
    try {
      if (editingCategory) {
        await adminAPI.updateCategory(editingCategory.id, categoryForm);
        toast.success('Category updated!');
      } else {
        await adminAPI.createCategory(categoryForm);
        toast.success('Category created!');
      }
      setShowCategoryDialog(false);
      setEditingCategory(null);
      resetCategoryForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? Tours in this category will be moved to "other".')) return;
    
    setLoading(true);
    try {
      await adminAPI.deleteCategory(categoryId);
      toast.success('Category deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      icon: category.icon || 'Compass',
      color: category.color || '#22c55e',
      image_url: category.image_url || '',
      is_active: category.is_active,
      sort_order: category.sort_order || 0,
    });
    setShowCategoryDialog(true);
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '', slug: '', description: '', icon: 'Compass', color: '#22c55e',
      image_url: '', is_active: true, sort_order: categories.length + 1
    });
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  // Quick assign from tour card
  const openAssignDialog = (tour) => {
    setSelectedTour(tour);
    setSelectedCategory(tour.category || '');
    setShowAssignDialog(true);
  };

  // Add tours to category
  const openAddTourDialog = (category) => {
    setAddingToCategory(category);
    setShowAddTourDialog(true);
  };

  const handleAddTourToCategory = async (tourId) => {
    if (!addingToCategory) return;
    
    setLoading(true);
    try {
      await adminAPI.updateTour(tourId, { category: addingToCategory.slug });
      toast.success('Tour added to category');
      onRefresh();
    } catch (error) {
      toast.error('Failed to add tour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="unified-tours-tab" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Tours & Categories</h1>
          <p className="text-muted-foreground">Manage tours and organize them by category</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => {
              resetCategoryForm();
              setEditingCategory(null);
              setShowCategoryDialog(true);
            }}
            className="gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            New Category
          </Button>
          <Button onClick={() => onEditTour(null)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Tour
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MapPin className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{tours.length}</p>
              <p className="text-sm text-muted-foreground">Total Tours</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{tours.filter(t => t.is_active).length}</p>
              <p className="text-sm text-muted-foreground">Active Tours</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FolderOpen className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{categories.length}</p>
              <p className="text-sm text-muted-foreground">Categories</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Star className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{tours.filter(t => t.is_featured).length}</p>
              <p className="text-sm text-muted-foreground">Featured</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="tours">All Tours</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        {/* Tours Tab */}
        <TabsContent value="tours" className="space-y-4">
          {/* Search & Filter */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tours..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tours List */}
          {filteredTours.length === 0 ? (
            <Card className="p-12 text-center">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No tours found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || categoryFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first tour to get started'}
              </p>
              <Button onClick={() => onEditTour(null)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tour
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredTours.map((tour) => (
                <Card key={tour.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`tour-card-${tour.id}`}>
                  <div className="flex items-center gap-4">
                    <img 
                      src={tour.image_url} 
                      alt={tour.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold truncate">{tour.title}</h3>
                        <Badge className={tour.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                          {tour.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {tour.is_featured && <Badge className="bg-amber-100 text-amber-700">Featured</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          <span className="capitalize">{tour.category || 'Uncategorized'}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {tour.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {tour.duration}
                        </span>
                      </div>
                      <p className="font-bold text-primary mt-1">{formatPrice(tour.price)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openAssignDialog(tour)}
                        className="gap-1"
                        data-testid={`assign-category-${tour.id}`}
                      >
                        <FolderOpen className="h-4 w-4" />
                        Category
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onEditTour(tour)}
                        className="gap-1"
                        data-testid={`edit-tour-${tour.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => onDeleteTour(tour.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => {
                resetCategoryForm();
                setEditingCategory(null);
                setShowCategoryDialog(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          </div>

          {categories.length === 0 ? (
            <Card className="p-12 text-center">
              <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No categories yet</h3>
              <p className="text-muted-foreground mb-4">Create categories to organize your tours</p>
              <Button onClick={() => setShowCategoryDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {categories.map((category) => {
                const categoryTours = getToursInCategory(category.slug);
                const isExpanded = expandedCategory === category.id;

                return (
                  <Card key={category.id} className={`overflow-hidden ${!category.is_active ? 'opacity-60' : ''}`}>
                    {/* Category Header */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${category.color}20`, color: category.color }}
                        >
                          <FolderOpen className="h-6 w-6" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{category.name}</h3>
                            <Badge style={{ backgroundColor: `${category.color}20`, color: category.color }}>
                              {category.slug}
                            </Badge>
                            <Badge variant="outline">{categoryTours.length} tours</Badge>
                          </div>
                          {category.description && (
                            <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openAddTourDialog(category);
                            }}
                            className="gap-1"
                          >
                            <Plus className="h-4 w-4" />
                            Add Tour
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCategory(category);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCategory(category.id);
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Tours List */}
                    {isExpanded && (
                      <div className="border-t bg-muted/20 p-4">
                        {categoryTours.length === 0 ? (
                          <p className="text-muted-foreground text-center py-4">
                            No tours in this category
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {categoryTours.map((tour) => (
                              <div 
                                key={tour.id}
                                className="flex items-center justify-between p-3 bg-white rounded-lg border"
                              >
                                <div className="flex items-center gap-3">
                                  <img 
                                    src={tour.image_url} 
                                    alt={tour.title}
                                    className="w-12 h-12 object-cover rounded"
                                  />
                                  <div>
                                    <p className="font-medium">{tour.title}</p>
                                    <p className="text-sm text-muted-foreground">{formatPrice(tour.price)}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => onEditTour(tour)}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleRemoveFromCategory(tour.id)}
                                    className="text-red-600"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Assign Category Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Category</DialogTitle>
          </DialogHeader>
          
          {selectedTour && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <img 
                  src={selectedTour.image_url} 
                  alt={selectedTour.title}
                  className="w-16 h-16 object-cover rounded"
                />
                <div>
                  <p className="font-semibold">{selectedTour.title}</p>
                  <p className="text-sm text-muted-foreground">Current: {selectedTour.category || 'None'}</p>
                </div>
              </div>

              <div>
                <Label>Select Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.slug}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignCategory} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Form Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setCategoryForm({
                    ...categoryForm, 
                    name,
                    slug: editingCategory ? categoryForm.slug : generateSlug(name)
                  });
                }}
                placeholder="Adventure Tours"
              />
            </div>

            <div>
              <Label>Slug *</Label>
              <Input
                value={categoryForm.slug}
                onChange={(e) => setCategoryForm({...categoryForm, slug: e.target.value})}
                placeholder="adventure"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                placeholder="Exciting outdoor adventures"
              />
            </div>

            <div>
              <Label>Icon</Label>
              <Select 
                value={categoryForm.icon} 
                onValueChange={(val) => setCategoryForm({...categoryForm, icon: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 ${categoryForm.color === color ? 'border-gray-900' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setCategoryForm({...categoryForm, color})}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label>Image URL</Label>
              <Input
                value={categoryForm.image_url}
                onChange={(e) => setCategoryForm({...categoryForm, image_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingCategory ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tour to Category Dialog */}
      <Dialog open={showAddTourDialog} onOpenChange={setShowAddTourDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Tour to {addingToCategory?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4 max-h-96 overflow-y-auto">
            {tours.filter(t => t.category !== addingToCategory?.slug).map((tour) => (
              <div 
                key={tour.id}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <img 
                    src={tour.image_url} 
                    alt={tour.title}
                    className="w-12 h-12 object-cover rounded"
                  />
                  <div>
                    <p className="font-medium">{tour.title}</p>
                    <p className="text-sm text-muted-foreground capitalize">{tour.category || 'Uncategorized'}</p>
                  </div>
                </div>
                <Button 
                  size="sm"
                  onClick={() => handleAddTourToCategory(tour.id)}
                  disabled={loading}
                >
                  Add
                </Button>
              </div>
            ))}
            {tours.filter(t => t.category !== addingToCategory?.slug).length === 0 && (
              <p className="text-center text-muted-foreground py-4">All tours are already in this category</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTourDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
